import { useEffect, useMemo, useState } from 'react'
import { useEquipmentScope } from './equipmentScope'
import { Eye, Link2, SlidersHorizontal, Unlink } from 'lucide-react'
import { ProcedurePreviewDialog } from '@/components/procedures/ProcedurePreviewDialog'
import { ConnectTemplateDialog } from '@/components/procedures/ConnectTemplateDialog'
import { procedureTypeForPurpose } from '@/lib/equipment'
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  CYCLE_LABELS,
  DAY_PICKER_CYCLES,
  eventSoftStyle,
  PREFERRED_TIMING_OPTIONS,
  WEEKDAY_LABELS,
  WEEKDAY_NAMES,
  type Cycle,
  type CycleProcedures,
  type Equipment,
  type EventType,
  type WeeklyDayState,
} from '@/types/equipment'
import type { EventTypeRow } from '@/hooks/useEventTypes'
import { isSchedulableEventType } from '@/types/maintenanceTask'

export type ConfigureTarget = {
  equipment: Equipment
  cycle: Cycle
  /**
   * Opened from the Type tab. A cycle's procedures are linked to the TYPE and
   * inherited by every unit of it (client: "cleaning procedures link to room
   * type, not individual rooms, mirroring equipment PM"), so they are linked
   * only from there. Opened from a unit's row, the dialog shows them read-only.
   */
  fromType?: boolean
}

const NO_PREFERENCE = 'No preference'

// tri-state weekday: 0 = off, 1 = required, 2 = preferred (★)
function buildDayNote(dayStates: number[]): string | undefined {
  const required = dayStates.filter((s) => s >= 1).length
  if (required === 0) return undefined
  const preferred = dayStates
    .map((s, i) => (s === 2 ? `★${WEEKDAY_NAMES[i]}` : null))
    .filter(Boolean)
    .join(' ')
  return preferred ? `${required}×/wk · ${preferred}` : `${required}×/wk`
}

export function ConfigureScheduleModal({
  target,
  eventTypes,
  onClose,
  onSave,
}: {
  target: ConfigureTarget | null
  eventTypes: EventTypeRow[]
  onClose: () => void
  onSave: (
    cycle: Cycle,
    eventTypes: EventType[],
    note?: string,
    weeklyDays?: WeeklyDayState[],
    preferredTiming?: string,
    startTime?: string,
    endTime?: string,
    /** The procedure each event type runs (z8nrz7f9u6): the whole set; undefined leaves them as they are. */
    procedures?: CycleProcedures,
  ) => void
}) {
  const { copy } = useEquipmentScope()
  // "Connecting Rooms to Procedure = Cleaning, Connecting Equipment to
  // Procedure = Equipment" — which templates this cycle's event types may run.
  const procedureType = procedureTypeForPurpose(target?.equipment.purpose ?? 'PROCESSING')
  const canLink = target?.fromType === true
  const cycle = target?.cycle
  const usesDayPicker = cycle ? DAY_PICKER_CYCLES.includes(cycle) : false
  const timingOptions = cycle ? PREFERRED_TIMING_OPTIONS[cycle] : undefined

  const [selected, setSelected] = useState<Set<EventType>>(new Set())
  const [dayStates, setDayStates] = useState<number[]>(() => Array(7).fill(0))
  const [timing, setTiming] = useState<string>(NO_PREFERENCE)
  const [startTime, setStartTime] = useState<string>('')
  const [endTime, setEndTime] = useState<string>('')
  // One procedure per event type (z8nrz7f9u6): monthly PM one form, monthly
  // calibration another. Staged here and saved with the schedule, like every
  // other field of the cycle.
  const [procedures, setProcedures] = useState<CycleProcedures>({})
  const [proceduresChanged, setProceduresChanged] = useState(false)
  // The event type whose procedure is being picked.
  const [linkingFor, setLinkingFor] = useState<EventType | null>(null)
  // The linked procedure being previewed (z8nrz7fb9k).
  const [preview, setPreview] = useState<{ id: string; name: string; event: string } | null>(null)

  // Reseed whenever a new cell is opened — restore its saved events, days and timing.
  useEffect(() => {
    if (!target) return
    const entry = target.equipment.schedule[target.cycle]
    setSelected(new Set(entry?.eventTypes ?? []))
    const days = Array(7).fill(0)
    for (const d of entry?.weeklyDays ?? []) days[d.day] = d.state === 'preferred' ? 2 : 1
    setDayStates(days)
    setTiming(entry?.preferredTiming ?? NO_PREFERENCE)
    setStartTime(entry?.startTime ?? '')
    setEndTime(entry?.endTime ?? '')
    setProcedures(entry?.procedures ?? {})
    setProceduresChanged(false)
    setLinkingFor(null)
    setPreview(null)
  }, [target])

  // A window requires end strictly after start; a lone start (or nothing) is fine.
  const timeError = startTime && endTime && endTime <= startTime

  const cycleLabel = cycle ? CYCLE_LABELS[cycle] : ''

  const toggleEvent = (type: EventType) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const setProcedureFor = (code: EventType, template: { id: string; name: string } | null) => {
    setProcedures((prev) => {
      const next = { ...prev }
      if (template) next[code] = template
      else delete next[code]
      return next
    })
    setProceduresChanged(true)
  }

  const cycleDay = (i: number) => {
    setDayStates((prev) => prev.map((s, idx) => (idx === i ? (s + 1) % 3 : s)))
  }

  // The catalog carries display-only codes (`process`) that the server refuses
  // on a schedule cell, so they are neither offered nor savable from here.
  const schedulableTypes = useMemo(
    () => eventTypes.filter((t) => isSchedulableEventType(t.code)),
    [eventTypes],
  )

  const labelOf = (code: EventType | null) =>
    (code && schedulableTypes.find((t) => t.code === code)?.label) || 'this event type'

  const orderedSelection = useMemo(
    () => schedulableTypes.map((t) => t.code).filter((code) => selected.has(code)),
    [schedulableTypes, selected],
  )

  const handleSave = () => {
    if (!cycle) return
    let note: string | undefined
    let weeklyDays: WeeklyDayState[] | undefined
    let preferredTiming: string | undefined
    if (usesDayPicker) {
      note = buildDayNote(dayStates)
      const days = dayStates
        .map((state, day) => ({ day, state: state === 2 ? ('preferred' as const) : ('required' as const), on: state > 0 }))
        .filter((d) => d.on)
        .map(({ day, state }) => ({ day, state }))
      weeklyDays = days.length ? days : undefined
    } else if (timingOptions && timing !== NO_PREFERENCE) {
      note = timing
      preferredTiming = timing
    }
    if (timeError) return
    onSave(
      cycle,
      orderedSelection,
      note,
      weeklyDays,
      preferredTiming,
      startTime || undefined,
      endTime || undefined,
      // An event type taken off the cycle takes its procedure with it.
      proceduresChanged
        ? Object.fromEntries(orderedSelection.flatMap((code) => (procedures[code] ? [[code, procedures[code]]] : [])))
        : undefined,
    )
    onClose()
  }

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-xl'>
        {target && (
          <>
            <div className='flex items-start gap-3'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600'>
                <SlidersHorizontal className='h-5 w-5' />
              </div>
              <div>
                <h2 className='text-base font-semibold text-gray-900'>
                  Configure {cycleLabel} schedule
                </h2>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  Define the events performed on the <span className='font-medium'>{cycleLabel}</span> cycle.
                </p>
              </div>
            </div>

            <div className='mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800'>
              Applies to {copy.type}: {target.equipment.equipmentType} — all {copy.unitMany}
            </div>

            <p className='mt-5 text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
              Event types
            </p>
            <div className='mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2'>
              {schedulableTypes.map((type) => {
                const checked = selected.has(type.code)
                const linked = procedures[type.code]
                return (
                  <div
                    key={type.code}
                    style={checked ? eventSoftStyle(type.color) : undefined}
                    className={cn(
                      'flex flex-col rounded-lg border text-sm transition-colors',
                      checked ? 'font-medium' : 'border-gray-200 text-gray-700 hover:bg-gray-50',
                    )}
                    data-testid={`cycle-event-${type.code}`}
                  >
                    <div className='flex items-center'>
                      <button
                        type='button'
                        onClick={() => toggleEvent(type.code)}
                        className='flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left'
                      >
                        <span
                          style={checked ? { backgroundColor: type.color } : undefined}
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                            checked ? 'border-transparent' : 'border-gray-300 bg-white',
                          )}
                        >
                          {checked && (
                            <svg viewBox='0 0 10 10' className='h-2.5 w-2.5 fill-none stroke-white stroke-2'>
                              <path d='M1.5 5.2 4 7.5 8.5 2.5' strokeLinecap='round' strokeLinejoin='round' />
                            </svg>
                          )}
                        </span>
                        <span className='truncate'>{type.label}</span>
                      </button>
                      {/* "The link should be next to calibration, next to PM": each
                          event type on the cycle runs its own form (z8nrz7f9u6). */}
                      {checked && canLink && (
                        <button
                          type='button'
                          onClick={() => setLinkingFor(type.code)}
                          // Hovering names the procedure (z8nrz7fb9k); clicking changes it.
                          title={linked ? `${linked.name} — click to change` : `Link a ${procedureType} procedure to ${type.label}`}
                          aria-label={`Link a procedure to ${type.label}`}
                          className='mr-1.5 shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-white/70 hover:text-gray-900'
                        >
                          <Link2 className='h-4 w-4' />
                        </button>
                      )}
                    </div>
                    {checked && (
                      <div className='-mt-1 flex min-w-0 items-center gap-1 pb-2 pl-9 pr-2 text-xs font-normal' data-testid='cycle-procedure'>
                        {linked ? (
                          <>
                            {/* Click the linked procedure to preview its forms (z8nrz7fb9k). */}
                            <button
                              type='button'
                              onClick={() => setPreview({ ...linked, event: type.label })}
                              title={`Preview ${linked.name}`}
                              aria-label={`Preview ${linked.name}`}
                              className='inline-flex min-w-0 items-center gap-1 rounded text-gray-700 hover:text-blue-700 hover:underline'
                            >
                              <Eye className='h-3.5 w-3.5 shrink-0 text-gray-400' />
                              <span className='truncate'>{linked.name}</span>
                            </button>
                            {canLink && (
                              <button
                                type='button'
                                onClick={() => setProcedureFor(type.code, null)}
                                title={`Unlink ${linked.name} from ${type.label}`}
                                aria-label={`Unlink the procedure from ${type.label}`}
                                className='shrink-0 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-600'
                              >
                                <Unlink className='h-3.5 w-3.5' />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className='text-gray-400'>No procedure linked</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {usesDayPicker ? (
              <div className='mt-5'>
                <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
                  Days to perform &amp; preferred days
                </p>
                <div className='mt-2 grid grid-cols-7 gap-1.5'>
                  {WEEKDAY_LABELS.map((label, i) => {
                    const state = dayStates[i]
                    return (
                      <button
                        key={i}
                        type='button'
                        onClick={() => cycleDay(i)}
                        title={WEEKDAY_NAMES[i]}
                        className={cn(
                          'relative flex h-11 items-center justify-center rounded-lg border text-sm font-medium transition-colors',
                          state === 0 && 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                          state === 1 && 'border-transparent bg-blue-600 text-white',
                          state === 2 && 'border-transparent bg-green-600 text-white',
                        )}
                      >
                        {label}
                        {state === 2 && <span className='absolute right-1 top-0.5 text-[10px]'>★</span>}
                      </button>
                    )
                  })}
                </div>
                <p className='mt-2 text-[11px] italic text-gray-400'>
                  Click a day once = required (blue), twice = preferred ★ (green), third = clear.
                </p>
              </div>
            ) : timingOptions ? (
              <div className='mt-5'>
                <label className='text-xs font-medium text-gray-600'>Preferred timing within period</label>
                <Select value={timing} onValueChange={setTiming}>
                  <SelectTrigger className='mt-1.5 w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PREFERENCE}>{NO_PREFERENCE}</SelectItem>
                    {timingOptions.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className='mt-5'>
              <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-500'>
                Time (optional)
              </p>
              <p className='mt-0.5 text-[11px] text-gray-400'>
                Leave blank for an all-day event. Set a start time, and optionally an end time for a window.
              </p>
              <div className='mt-2 flex flex-wrap items-center gap-2'>
                <label className='flex flex-col text-xs font-medium text-gray-600'>
                  <span className='mb-1'>Time</span>
                  <input
                    type='time'
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className='rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none'
                  />
                </label>
                <span className='mt-5 text-xs text-gray-400'>to</span>
                <label className='flex flex-col text-xs font-medium text-gray-600'>
                  <span className='mb-1'>End (optional)</span>
                  <input
                    type='time'
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    disabled={!startTime}
                    className='rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400'
                  />
                </label>
              </div>
              {timeError && (
                <p className='mt-1.5 text-[11px] font-medium text-red-600'>
                  End time must be after the start time.
                </p>
              )}
            </div>

            {canLink && (
              <ConnectTemplateDialog
                open={linkingFor !== null}
                onClose={() => setLinkingFor(null)}
                templateType={procedureType}
                currentTemplateId={linkingFor ? (procedures[linkingFor]?.id ?? null) : null}
                description={`Link the ${procedureType} procedure that ${labelOf(linkingFor)} runs on the ${cycleLabel.toLowerCase()} cycle of ${target.equipment.equipmentType} — all ${copy.unitMany}. Saved with the schedule.`}
                onConnect={(template) => {
                  if (linkingFor) setProcedureFor(linkingFor, template)
                  setLinkingFor(null)
                }}
                onDisconnect={() => {
                  if (linkingFor) setProcedureFor(linkingFor, null)
                }}
              />
            )}

            {preview && (
              <ProcedurePreviewDialog
                procedure={preview}
                context={`${preview.event} · ${cycleLabel} · ${target.equipment.equipmentType}`}
                onClose={() => setPreview(null)}
              />
            )}

            <DialogFooter className='mt-6 sm:justify-between'>
              {canLink ? (
                <span className='text-xs text-gray-400'>
                  <Link2 className='mr-1 inline h-3.5 w-3.5 align-[-2px]' />
                  Link a procedure next to each event type.
                </span>
              ) : (
                // Read-only on a unit's row: the procedures belong to its type.
                <span
                  className='flex min-w-0 items-center gap-1.5 text-xs text-gray-500'
                  title={`Procedures are linked per ${copy.type}, on the ${copy.type} tab`}
                >
                  <Link2 className='h-3.5 w-3.5 shrink-0' />
                  <span className='truncate'>Procedures are linked on the {copy.type} tab</span>
                </span>
              )}
              <div className='flex flex-col-reverse gap-2 sm:flex-row'>
                <Button variant='outline' onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={!!timeError}>
                  Save schedule
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
